/**
 * Haptic feedback utilities for the Cannabias review form.
 *
 * Cross-platform approach that works on both Android AND iOS Safari:
 *
 * 1. Android Chrome/WebView: Uses navigator.vibrate() (Vibration API)
 * 2. iOS Safari: Uses a hidden native <input type="checkbox"> toggle trick.
 *    When a native checkbox is programmatically toggled on iOS Safari, the
 *    system taptic engine fires a subtle haptic bump. This is the same
 *    approach used by many web apps to achieve iOS haptics without a native
 *    wrapper.
 *
 * Duolingo-level feedback: every interaction that changes a value should
 * give the user a satisfying tactile bump.
 */

// ── Platform detection ──────────────────────────────────────────────

const isIOS = (): boolean =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !(window as any).MSStream;

const canVibrate = (): boolean =>
  typeof navigator !== "undefined" && "vibrate" in navigator;

// ── iOS haptic engine (hidden checkbox trick) ───────────────────────

let iosCheckbox: HTMLInputElement | null = null;

function getIOSCheckbox(): HTMLInputElement {
  if (iosCheckbox) return iosCheckbox;

  const el = document.createElement("input");
  el.type = "checkbox";
  el.style.position = "fixed";
  el.style.left = "-9999px";
  el.style.top = "-9999px";
  el.style.opacity = "0";
  el.style.pointerEvents = "none";
  el.style.width = "0";
  el.style.height = "0";
  el.setAttribute("aria-hidden", "true");
  el.tabIndex = -1;
  document.body.appendChild(el);
  iosCheckbox = el;
  return el;
}

/**
 * Trigger a single iOS haptic tap by toggling a hidden native checkbox.
 * For stronger/multiple pulses, call multiple times with a delay.
 */
function iosHapticTap(): void {
  try {
    const cb = getIOSCheckbox();
    cb.checked = !cb.checked;
    // Force layout recalculation to ensure the toggle registers
    cb.offsetHeight;
  } catch {
    // Silently fail — no haptic is better than an error
  }
}

/**
 * Fire multiple iOS haptic taps with spacing to create patterns.
 */
function iosHapticPattern(taps: number, intervalMs: number = 80): void {
  for (let i = 0; i < taps; i++) {
    setTimeout(() => iosHapticTap(), i * intervalMs);
  }
}

// ── Cross-platform haptic functions ─────────────────────────────────

/** Light notch – used when a slider crosses a step boundary. */
export function hapticNotch(): void {
  if (isIOS()) {
    iosHapticTap();
    return;
  }
  if (canVibrate()) {
    navigator.vibrate(8);
  }
}

/** Slightly firmer tap – used for star rating selection. */
export function hapticStarTap(): void {
  if (isIOS()) {
    iosHapticTap();
    return;
  }
  if (canVibrate()) {
    navigator.vibrate(12);
  }
}

/** Medium tap – used for binary / toggle buttons (packaging, best-used, buy-again). */
export function hapticToggleTap(): void {
  if (isIOS()) {
    iosHapticTap();
    return;
  }
  if (canVibrate()) {
    navigator.vibrate(15);
  }
}

/** Double-tap feel – used when moving to the next step. */
export function hapticStepTransition(): void {
  if (isIOS()) {
    iosHapticPattern(2, 60);
    return;
  }
  if (canVibrate()) {
    navigator.vibrate([10, 40, 10]);
  }
}

// ── Celebration patterns ────────────────────────────────────────────

/**
 * 🎆  Fireworks mode – a long, escalating burst pattern that plays on
 * successful review submission.  Think Duolingo streak celebration.
 *
 * iOS: rapid succession of checkbox toggles creating a buzz effect
 * Android: escalating vibration pattern
 */
export function hapticFireworks(): void {
  if (isIOS()) {
    // Opening crackle (3 quick taps)
    iosHapticPattern(3, 50);
    // Building momentum (4 taps, slightly faster)
    setTimeout(() => iosHapticPattern(4, 40), 200);
    // Crescendo (5 rapid taps)
    setTimeout(() => iosHapticPattern(5, 30), 400);
    // Big burst (6 very rapid taps)
    setTimeout(() => iosHapticPattern(6, 25), 600);
    // Final bloom (3 spaced taps)
    setTimeout(() => iosHapticPattern(3, 100), 800);
    return;
  }
  if (canVibrate()) {
    navigator.vibrate([
      // Opening crackle
      15, 30, 15, 30, 15, 30,
      // Building momentum
      20, 25, 20, 25, 25, 20, 25, 20,
      // Crescendo
      30, 15, 35, 15, 40, 15,
      // Big burst
      50, 10, 60, 10, 80, 30,
      // Final bloom
      100, 40, 120,
    ]);
  }
}

/**
 * Golden review celebration – even more dramatic than standard fireworks.
 * Plays when a golden review (1-in-100) is submitted.
 */
export function hapticGoldenFireworks(): void {
  if (isIOS()) {
    // Drumroll (rapid light taps)
    iosHapticPattern(6, 35);
    // Build
    setTimeout(() => iosHapticPattern(6, 30), 250);
    // Rapid crescendo
    setTimeout(() => iosHapticPattern(8, 25), 500);
    // Explosion burst
    setTimeout(() => iosHapticPattern(8, 20), 750);
    // Afterglow pulses
    setTimeout(() => iosHapticPattern(4, 120), 1000);
    return;
  }
  if (canVibrate()) {
    navigator.vibrate([
      // Drumroll
      10, 20, 10, 20, 10, 20, 10, 20,
      10, 15, 10, 15, 10, 15, 10, 15,
      // Rapid build
      15, 10, 15, 10, 20, 10, 20, 10,
      25, 10, 25, 10, 30, 10, 30, 10,
      // Explosion
      50, 5, 60, 5, 70, 5, 80, 5,
      // Afterglow pulses
      100, 60, 80, 60, 120, 80, 150,
    ]);
  }
}

/**
 * Cancel any running vibration (useful if user navigates away mid-celebration).
 */
export function hapticCancel(): void {
  if (canVibrate()) {
    navigator.vibrate(0);
  }
}
